import { formatTRY } from "@/lib/currency";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import {
  QrCode,
  Save,
  Upload,
  Plus,
  Trash2,
  Settings,
  Palette,
  Globe,
  Building2,
  Phone,
  MessageSquare,
  MapPin,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Smartphone,
  Sparkles,
  Calendar,
  ChevronRight,
  Image as ImageIcon,
  Search,
  Heart,
  Info,
  Clock,
  Utensils,
  ChevronDown,
  Check,
  RotateCcw,
  FileText,
  AlertCircle,
  TrendingUp,
  Printer,
  Download,
  Wifi,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@pos-vidyon/ui/components/button";
import { Switch } from "@pos-vidyon/ui/components/switch";
import { Separator } from "@pos-vidyon/ui/components/separator";
import {
  categories as menuCategories,
  products as menuProducts,
  getProductImage,
} from "@/components/dashboard/data";
import {
  QR_THEME_PRESETS,
  DEFAULT_QR_THEME,
  QR_FONT_OPTIONS,
  getQrFontStack,
  ensureQrFontLoaded,
  type QrThemePreset,
} from "@/lib/qr-menu-theme";

export const Route = createFileRoute("/_auth/admin/qr-menu")({
  component: QRMenuManagementPage,
});

// --- Types ---
interface MenuConfig {
  id: string;
  name: string;
  visible: boolean;
  schedule: string;
  categoryCount: number;
}

interface BannerConfig {
  id: string;
  title: string;
  image: string;
  dateRange: string;
  visible: boolean;
  priority: number;
}

interface TableQR {
  tableNumber: string;
  code: string;
}

// Tema şablonları @/lib/qr-menu-theme'den gelir (tek doğruluk kaynağı);
// varsayılan renk/tipografi değerleri her zaman şablondan okunur.
const THEME_PRESETS = QR_THEME_PRESETS;

// --- Preview helpers: /menu sayfasındaki gerçek kategori & ürün verisi ---
const PREVIEW_CATEGORY_LABELS: Record<string, string> = {
  coffee: "Nitelikli Kahveler",
  tea: "Gurme Bitki Çayları",
  "cold-drinks": "El Yapımı Soğuk İçecekler",
  bakery: "Günlük Fırın Ürünleri",
  desserts: "Sanatsal Tatlılar",
  sandwiches: "Gurme Sandviçler",
};

const PREVIEW_CATEGORY_STYLES: Record<string, { bg: string; text: string; badge: string }> = {
  favorites: { bg: "bg-amber-50", text: "text-amber-600", badge: "bg-amber-100/70 text-amber-800" },
  coffee: { bg: "bg-amber-100/50", text: "text-amber-800", badge: "bg-amber-100 text-amber-900" },
  tea: { bg: "bg-emerald-50", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-800" },
  "cold-drinks": { bg: "bg-sky-50", text: "text-sky-700", badge: "bg-sky-100 text-sky-800" },
  bakery: { bg: "bg-orange-50", text: "text-orange-700", badge: "bg-orange-100 text-orange-800" },
  desserts: { bg: "bg-rose-50", text: "text-rose-700", badge: "bg-rose-100 text-rose-800" },
};

// Hex rengi verilen opaklıkla rgba'ya çevirir (önizlemedeki soft rozet zeminleri için)
function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.replace("#", "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return `rgba(217, 119, 6, ${alpha})`;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Görsel Arayüz bölümündeki pasif ayarlar için "yakında" rozeti
const ComingSoonBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[9px] font-bold text-amber-700 whitespace-nowrap">
    <AlertCircle className="size-2.5" /> QR menüde yakında
  </span>
);

export function QRMenuManagementPage() {
  // --- Active Tab for Left Customizer ---
  const [activeSection, setActiveSection] = useState<
    | "status"
    | "menus"
    | "themes"
    | "appearance"
    | "business"
    | "languages"
    | "banners"
    | "qrcodes"
    | "analytics"
    | "advanced"
  >("status");

  // --- Active Tab for Right Mobil Preview ("kapak" | "menu") ---
  const [previewTab, setPreviewTab] = useState<"kapak" | "menu">("kapak");

  // --- 1. Status State ---
  const [isEnabled, setIsEnabled] = useState(true);
  const [isPublished, setIsPublished] = useState(true);
  const [lastUpdated, setLastUpdated] = useState("Bugün 20:45");
  const [publicMenuUrl, setPublicMenuUrl] = useState("https://qr.vidyon.co/menuler/cafe-monaco");

  // --- 2. Menus Configuration State ---
  const [menus, setMenus] = useState<MenuConfig[]>([
    {
      id: "1",
      name: "Kahvaltı Menüsü",
      visible: true,
      schedule: "Her gün 08:00 - 12:00",
      categoryCount: 4,
    },
    { id: "2", name: "Ana Yemekler", visible: true, schedule: "Tüm Gün", categoryCount: 8 },
    { id: "3", name: "Tatlı & Fırın", visible: true, schedule: "Tüm Gün", categoryCount: 5 },
    {
      id: "4",
      name: "Sıcak & Soğuk İçecekler",
      visible: true,
      schedule: "Tüm Gün",
      categoryCount: 9,
    },
    {
      id: "5",
      name: "Kokteyl Menüsü",
      visible: false,
      schedule: "18:00 Sonrası",
      categoryCount: 3,
    },
    {
      id: "6",
      name: "Mevsimsel Lezzetler",
      visible: false,
      schedule: "Sınırlı Süre",
      categoryCount: 2,
    },
  ]);

  // --- 3. Themes Selector State ---
  const [selectedThemeId, setSelectedThemeId] = useState(DEFAULT_QR_THEME.id);

  // --- 4. Appearance Config State ---
  const [logoUrl, setLogoUrl] = useState(
    "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=200&auto=format&fit=crop&q=80",
  );
  const [coverImageUrl, setCoverImageUrl] = useState(
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&auto=format&fit=crop&q=80",
  );
  // Varsayılan görünüm değerleri aktif tema şablonundan gelir
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_QR_THEME.primary);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_QR_THEME.secondary);
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_QR_THEME.background);
  const [borderRadius, setBorderRadius] = useState(DEFAULT_QR_THEME.radius);
  const [fontFamily, setFontFamily] = useState(DEFAULT_QR_THEME.font);
  const [buttonStyle, setButtonStyle] = useState<"sharp" | "rounded" | "pill">(
    DEFAULT_QR_THEME.buttonStyle,
  );
  const [cardStyle, setCardStyle] = useState<"flat" | "elevated" | "glass">(
    DEFAULT_QR_THEME.cardStyle,
  );

  // Görünüm & davranış anahtarları — varsayılanlar tema şablonundan gelir
  const [showSearchBar, setShowSearchBar] = useState(DEFAULT_QR_THEME.showSearchBar);
  const [showCategoryIcons, setShowCategoryIcons] = useState(DEFAULT_QR_THEME.showCategoryIcons);
  const [showFeaturedProducts, setShowFeaturedProducts] = useState(
    DEFAULT_QR_THEME.showFeaturedProducts,
  );
  const [stickyCategories, setStickyCategories] = useState(DEFAULT_QR_THEME.stickyCategories);
  const [enableAnimations, setEnableAnimations] = useState(DEFAULT_QR_THEME.enableAnimations);

  // --- 5. Business Information State ---
  const [businessName, setBusinessName] = useState("Café Monaco");
  const [businessDescription, setBusinessDescription] = useState(
    "Premium kahve çekirdekleri, günlük el yapımı taze tatlılar ve zengin dünya mutfağı menümüzle Bebek sahilinde sizleri bekliyoruz.",
  );
  const [businessPhone, setBusinessPhone] = useState("+90 212 555 1234");
  const [businessWhatsApp, setBusinessWhatsApp] = useState("+90 532 555 5678");
  const [businessAddress, setBusinessAddress] = useState(
    "Bebek Mh., Cevdet Paşa Cd. No:45, Beşiktaş, İstanbul",
  );
  const [businessGmaps, setBusinessGmaps] = useState(
    "https://maps.google.com/?q=Café+Monaco+Bebek",
  );
  const [businessInstagram, setBusinessInstagram] = useState("@cafemonaco_istanbul");
  const [businessFacebook, setBusinessFacebook] = useState("cafemonaco.tr");
  const [businessWebsite, setBusinessWebsite] = useState("www.cafemonaco.com.tr");
  const [workingHours, setWorkingHours] = useState("Her gün: 08:00 - 23:30");

  // --- 6. Languages State ---
  const [isMultiLang, setIsMultiLang] = useState(true);
  const [defaultLanguage, setDefaultLanguage] = useState("tr");
  const [availableLanguages, setAvailableLanguages] = useState([
    { code: "tr", name: "Türkçe", enabled: true },
    { code: "en", name: "English", enabled: true },
    { code: "de", name: "Deutsch", enabled: false },
    { code: "ar", name: "العربية", enabled: false },
    { code: "ru", name: "Русский", enabled: false },
    { code: "es", name: "Español", enabled: false },
  ]);

  // --- 7. Promotional Banners State ---
  const [banners, setBanners] = useState<BannerConfig[]>([
    {
      id: "b1",
      title: "Yaz Kokteylleri Başladı! 🍹",
      image:
        "https://images.unsplash.com/photo-1513558161293-cdaf765ed2fd?w=600&auto=format&fit=crop&q=80",
      dateRange: "15 Haz - 31 Ağs",
      visible: true,
      priority: 1,
    },
    {
      id: "b2",
      title: "Pazar Kahvaltısı %15 İndirimli",
      image:
        "https://images.unsplash.com/photo-1496042401180-4743f550ba36?w=600&auto=format&fit=crop&q=80",
      dateRange: "Her Pazar 08:00-11:00",
      visible: true,
      priority: 2,
    },
  ]);
  const [showNewBannerForm, setShowNewBannerForm] = useState(false);
  const [newBannerTitle, setNewBannerTitle] = useState("");
  const [newBannerDates, setNewBannerDates] = useState("");

  // --- 8. QR Codes & Table QR Generation State ---
  const [tableQRList, setTableQRList] = useState<TableQR[]>([
    { tableNumber: "Masa 1", code: "M1-QR" },
    { tableNumber: "Masa 2", code: "M2-QR" },
    { tableNumber: "Masa 3", code: "M3-QR" },
    { tableNumber: "Masa 4", code: "M4-QR" },
    { tableNumber: "Masa 5", code: "M5-QR" },
    { tableNumber: "Masa 6", code: "M6-QR" },
    { tableNumber: "Masa 7", code: "M7-QR" },
    { tableNumber: "Masa 8", code: "M8-QR" },
  ]);
  const [newTableNumber, setNewTableNumber] = useState("");

  // --- 10. Advanced Settings State (varsayılanlar tema şablonundan) ---
  const [hidePrices, setHidePrices] = useState(DEFAULT_QR_THEME.hidePrices);
  const [hideUnavailable, setHideUnavailable] = useState(DEFAULT_QR_THEME.hideUnavailable);
  const [showAllergens, setShowAllergens] = useState(DEFAULT_QR_THEME.showAllergens);
  const [showCalories, setShowCalories] = useState(DEFAULT_QR_THEME.showCalories);
  const [seoTitle, setSeoTitle] = useState("Café Monaco - Dijital QR Menü");
  const [seoDescription, setSeoDescription] = useState(
    "En sevdiğiniz kahveler ve lezzetler tek tıkla masanızda.",
  );

  // --- File inputs elements helper references ---
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // --- Actions ---
  // Tema, QR menünün tüm görünüm ve davranışını belirleyen tek kaynaktır.
  // Uygulanınca renk/tipografiyle birlikte tüm görünüm anahtarlarını da temaya çeker.
  const handleApplyPresetTheme = (theme: QrThemePreset) => {
    setSelectedThemeId(theme.id);
    // Renk & tipografi
    setPrimaryColor(theme.primary);
    setSecondaryColor(theme.secondary);
    setBackgroundColor(theme.background);
    setFontFamily(theme.font);
    setBorderRadius(theme.radius);
    setButtonStyle(theme.buttonStyle);
    setCardStyle(theme.cardStyle);
    // Görünüm & davranış
    setShowSearchBar(theme.showSearchBar);
    setShowCategoryIcons(theme.showCategoryIcons);
    setShowFeaturedProducts(theme.showFeaturedProducts);
    setStickyCategories(theme.stickyCategories);
    setEnableAnimations(theme.enableAnimations);
    // Menü detay filtreleri
    setHidePrices(theme.hidePrices);
    setHideUnavailable(theme.hideUnavailable);
    setShowAllergens(theme.showAllergens);
    setShowCalories(theme.showCalories);
    toast.success(`${theme.name} teması uygulandı ve tüm görünüm ayarları güncellendi.`);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicMenuUrl);
    toast.success("Menü linki panoya kopyalandı!");
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setLogoUrl(event.target.result as string);
          toast.success("Yeni logo yüklendi.");
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setCoverImageUrl(event.target.result as string);
          toast.success("Kapak resmi güncellendi.");
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddBanner = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBannerTitle) return;
    const newBanner: BannerConfig = {
      id: `b-${Date.now()}`,
      title: newBannerTitle,
      image:
        "https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=600&auto=format&fit=crop&q=80",
      dateRange: newBannerDates || "Tüm Ay",
      visible: true,
      priority: banners.length + 1,
    };
    setBanners([...banners, newBanner]);
    setNewBannerTitle("");
    setNewBannerDates("");
    setShowNewBannerForm(false);
    toast.success("Yeni kampanya afişi eklendi!");
  };

  const handleAddTable = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTableNumber) return;
    if (tableQRList.some((t) => t.tableNumber.toLowerCase() === newTableNumber.toLowerCase())) {
      toast.error("Bu masa zaten ekli.");
      return;
    }
    setTableQRList([...tableQRList, { tableNumber: newTableNumber, code: `${newTableNumber}-QR` }]);
    setNewTableNumber("");
    toast.success(`${newTableNumber} için QR kod oluşturuldu.`);
  };

  const handleRemoveTable = (tableNumber: string) => {
    setTableQRList(tableQRList.filter((t) => t.tableNumber !== tableNumber));
    toast.success(`${tableNumber} listeden kaldırıldı.`);
  };

  useEffect(() => {
    // Dynamically set public menu link on load
    setPublicMenuUrl(window.location.origin + "/menu");

    // Load configurations from localStorage
    try {
      const saved = localStorage.getItem("qr_menu_settings");
      if (saved) {
        const s = JSON.parse(saved);
        if (s.isEnabled !== undefined) setIsEnabled(s.isEnabled);
        if (s.isPublished !== undefined) setIsPublished(s.isPublished);
        // Renk desteğinden önceki kayıtların renk/tipografi alanlarını yok say —
        // eski tema denemelerinden kalan koyu renkler varsayılan beyaz paleti bozmasın.
        const hasColorSupport = typeof s.settingsVersion === "number" && s.settingsVersion >= 2;
        if (hasColorSupport) {
          if (s.selectedThemeId !== undefined)
            setSelectedThemeId(
              THEME_PRESETS.some((t) => t.id === s.selectedThemeId) ||
                s.selectedThemeId === "custom"
                ? s.selectedThemeId
                : DEFAULT_QR_THEME.id,
            );
          if (s.primaryColor !== undefined) setPrimaryColor(s.primaryColor);
          if (s.secondaryColor !== undefined) setSecondaryColor(s.secondaryColor);
          if (s.backgroundColor !== undefined) setBackgroundColor(s.backgroundColor);
          if (s.borderRadius !== undefined) setBorderRadius(s.borderRadius);
          if (s.fontFamily !== undefined) setFontFamily(s.fontFamily);
          if (s.buttonStyle !== undefined) setButtonStyle(s.buttonStyle);
          if (s.cardStyle !== undefined) setCardStyle(s.cardStyle);
        }
        if (s.logoUrl !== undefined) setLogoUrl(s.logoUrl);
        if (s.coverImageUrl !== undefined) setCoverImageUrl(s.coverImageUrl);
        if (s.showSearchBar !== undefined) setShowSearchBar(s.showSearchBar);
        if (s.showCategoryIcons !== undefined) setShowCategoryIcons(s.showCategoryIcons);
        if (s.showFeaturedProducts !== undefined) setShowFeaturedProducts(s.showFeaturedProducts);
        if (s.stickyCategories !== undefined) setStickyCategories(s.stickyCategories);
        if (s.enableAnimations !== undefined) setEnableAnimations(s.enableAnimations);
        if (s.businessName !== undefined) setBusinessName(s.businessName);
        if (s.businessDescription !== undefined) setBusinessDescription(s.businessDescription);
        if (s.businessPhone !== undefined) setBusinessPhone(s.businessPhone);
        if (s.businessWhatsApp !== undefined) setBusinessWhatsApp(s.businessWhatsApp);
        if (s.businessAddress !== undefined) setBusinessAddress(s.businessAddress);
        if (s.businessGmaps !== undefined) setBusinessGmaps(s.businessGmaps);
        if (s.businessInstagram !== undefined) setBusinessInstagram(s.businessInstagram);
        if (s.businessFacebook !== undefined) setBusinessFacebook(s.businessFacebook);
        if (s.businessWebsite !== undefined) setBusinessWebsite(s.businessWebsite);
        if (s.workingHours !== undefined) setWorkingHours(s.workingHours);
        if (s.isMultiLang !== undefined) setIsMultiLang(s.isMultiLang);
        if (s.defaultLanguage !== undefined) setDefaultLanguage(s.defaultLanguage);
        if (s.availableLanguages !== undefined) setAvailableLanguages(s.availableLanguages);
        if (s.banners !== undefined) setBanners(s.banners);
        if (s.hidePrices !== undefined) setHidePrices(s.hidePrices);
        if (s.hideUnavailable !== undefined) setHideUnavailable(s.hideUnavailable);
        if (s.showAllergens !== undefined) setShowAllergens(s.showAllergens);
        if (s.showCalories !== undefined) setShowCalories(s.showCalories);
        if (s.seoTitle !== undefined) setSeoTitle(s.seoTitle);
        if (s.seoDescription !== undefined) setSeoDescription(s.seoDescription);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Seçili yazı tipini (gerekiyorsa) Google Fonts'tan yükle — önizleme anında değişsin
  useEffect(() => {
    ensureQrFontLoaded(fontFamily);
  }, [fontFamily]);

  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  const handleSaveAll = async () => {
    setIsSaving(true);
    const settings = {
      settingsVersion: 2,
      isEnabled,
      isPublished,
      selectedThemeId,
      logoUrl,
      coverImageUrl,
      primaryColor,
      secondaryColor,
      backgroundColor,
      borderRadius,
      fontFamily,
      buttonStyle,
      cardStyle,
      showSearchBar,
      showCategoryIcons,
      showFeaturedProducts,
      stickyCategories,
      enableAnimations,
      businessName,
      businessDescription,
      businessPhone,
      businessWhatsApp,
      businessAddress,
      businessGmaps,
      businessInstagram,
      businessFacebook,
      businessWebsite,
      workingHours,
      isMultiLang,
      defaultLanguage,
      availableLanguages,
      banners,
      hidePrices,
      hideUnavailable,
      showAllergens,
      showCalories,
      seoTitle,
      seoDescription,
    };
    try {
      // Simulate async save
      await new Promise((resolve) => setTimeout(resolve, 600));
      localStorage.setItem("qr_menu_settings", JSON.stringify(settings));
      const now = new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
      setLastUpdated(now);
      setLastSaved(now);
      toast.success("Tüm QR Menü ayarları başarıyla kaydedildi!");
    } catch {
      toast.error("Ayarlar kaydedilirken hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  // --- SVG QR Code Mock Generator ---
  const renderMockQRCode = (_text: string) => {
    return (
      <svg
        className="w-16 h-16 text-slate-800"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Border frame */}
        <rect width="100" height="100" rx="8" fill="white" stroke="#E2E8F0" strokeWidth="2" />
        {/* Outer boxes */}
        <rect x="12" y="12" width="22" height="22" rx="2" fill="currentColor" />
        <rect x="16" y="16" width="14" height="14" fill="white" />
        <rect x="19" y="19" width="8" height="8" fill="currentColor" />

        <rect x="66" y="12" width="22" height="22" rx="2" fill="currentColor" />
        <rect x="70" y="16" width="14" height="14" fill="white" />
        <rect x="73" y="19" width="8" height="8" fill="currentColor" />

        <rect x="12" y="66" width="22" height="22" rx="2" fill="currentColor" />
        <rect x="16" y="70" width="14" height="14" fill="white" />
        <rect x="19" y="73" width="8" height="8" fill="currentColor" />

        {/* Small alignment dot */}
        <rect x="66" y="66" width="10" height="10" rx="1" fill="currentColor" />
        <rect x="69" y="69" width="4" height="4" fill="white" />

        {/* Random dots to make it look like a real QR code */}
        <rect x="42" y="15" width="6" height="6" fill="currentColor" />
        <rect x="52" y="22" width="8" height="4" fill="currentColor" />
        <rect x="40" y="32" width="12" height="6" fill="currentColor" />
        <rect x="15" y="42" width="6" height="8" fill="currentColor" />
        <rect x="28" y="45" width="10" height="4" fill="currentColor" />
        <rect x="45" y="48" width="6" height="6" fill="currentColor" />
        <rect x="58" y="40" width="12" height="6" fill="currentColor" />
        <rect x="78" y="42" width="8" height="6" fill="currentColor" />
        <rect x="75" y="52" width="10" height="10" fill="currentColor" />

        <rect x="42" y="60" width="6" height="12" fill="currentColor" />
        <rect x="55" y="66" width="8" height="6" fill="currentColor" />
        <rect x="55" y="76" width="14" height="8" fill="currentColor" />
        <rect x="30" y="78" width="8" height="8" fill="currentColor" />
        <rect x="42" y="80" width="6" height="8" fill="currentColor" />
      </svg>
    );
  };

  const getNavItemClass = (section: typeof activeSection) => {
    const isActive = activeSection === section;
    return `flex items-center gap-2.5 w-full text-left px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
      isActive
        ? "bg-slate-900 text-white shadow-sm"
        : "text-slate-655 hover:bg-slate-50 hover:text-slate-900"
    }`;
  };

  const activeTheme = THEME_PRESETS.find((t) => t.id === selectedThemeId) ?? THEME_PRESETS[0];

  // Önizleme için türetilmiş biçim değerleri (canlı /menu ile aynı mantık, küçük ölçek)
  const previewFontStack = getQrFontStack(fontFamily);
  const previewChipRadius = buttonStyle === "sharp" ? 4 : buttonStyle === "rounded" ? 8 : 999;
  const previewCardRadius = Math.max(6, Math.round(borderRadius * 0.6));
  const previewCardShadow = cardStyle === "elevated" ? "shadow-xs" : "";
  const previewCardSurface =
    cardStyle === "glass"
      ? { backgroundColor: "rgba(255,255,255,0.6)", backdropFilter: "blur(8px)" }
      : { backgroundColor: "#FFFFFF" };

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
      {/* Sub-Header bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 bg-white px-6 py-5 shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-extrabold tracking-tight text-slate-900">
              QR Menü Yönetimi
            </h1>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Yayında
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Müşterilerinizin masada karekod okutarak ulaştığı dijital menü görünümünü ve detaylarını
            özelleştirin.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              toast.info("Tasarım sıfırlandı.");
              handleApplyPresetTheme(THEME_PRESETS[0]);
            }}
            variant="outline"
            className="h-9 text-xs border-slate-200 text-slate-650 hover:bg-slate-50 rounded-xl"
          >
            <RotateCcw className="size-3.5 mr-1" /> Sıfırla
          </Button>
          {lastSaved && !isSaving && (
            <span className="text-[10px] text-emerald-600 font-bold mr-1 hidden lg:inline-flex items-center gap-1">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              Son kayıt: {lastSaved}
            </span>
          )}
          <Button
            onClick={handleSaveAll}
            disabled={isSaving}
            className={`h-9 text-xs shadow-xs px-4 rounded-xl transition-all ${
              isSaving
                ? "bg-slate-350 text-white cursor-wait"
                : "bg-slate-900 text-white hover:bg-slate-800"
            }`}
          >
            {isSaving ? (
              <>
                <span className="size-3.5 mr-1.5 inline-block animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="size-3.5 mr-1.5" /> Değişiklikleri Kaydet
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main Builder Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Side: Builder Panels Index & Forms */}
        <div className="flex flex-1 overflow-hidden bg-white">
          {/* Vertical Navigation for Sections */}
          <nav className="w-60 border-r border-slate-100 bg-slate-50/30 p-4 flex flex-col gap-5 overflow-y-auto shrink-0">
            {/* Kurulum */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-2 block">
                Kurulum
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveSection("status")}
                  className={getNavItemClass("status")}
                >
                  <Smartphone className="size-4 shrink-0" />
                  <span>Kanal Durumu</span>
                </button>
                <button
                  onClick={() => setActiveSection("business")}
                  className={getNavItemClass("business")}
                >
                  <Building2 className="size-4 shrink-0" />
                  <span>İşletme Bilgileri</span>
                </button>
              </div>
            </div>

            {/* Görünüm */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-2 block">
                Görünüm
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveSection("themes")}
                  className={getNavItemClass("themes")}
                >
                  <Palette className="size-4 shrink-0" />
                  <span>Hazır Tasarım Temaları</span>
                </button>
                <button
                  onClick={() => setActiveSection("appearance")}
                  className={getNavItemClass("appearance")}
                >
                  <Settings className="size-4 shrink-0" />
                  <span>Görsel Arayüz</span>
                </button>
              </div>
            </div>

            {/* İçerik */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-2 block">
                İçerik
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveSection("menus")}
                  className={getNavItemClass("menus")}
                >
                  <Utensils className="size-4 shrink-0" />
                  <span>Menü Yapılandırması</span>
                </button>
                <button
                  onClick={() => setActiveSection("banners")}
                  className={getNavItemClass("banners")}
                >
                  <ImageIcon className="size-4 shrink-0" />
                  <span>Tanıtım & Duyuru Afişleri</span>
                </button>
                <button
                  onClick={() => setActiveSection("languages")}
                  className={getNavItemClass("languages")}
                >
                  <Globe className="size-4 shrink-0" />
                  <span>Çoklu Dil & Çeviri</span>
                </button>
              </div>
            </div>

            {/* Dağıtım */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-2 block">
                Dağıtım
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveSection("qrcodes")}
                  className={getNavItemClass("qrcodes")}
                >
                  <QrCode className="size-4 shrink-0" />
                  <span>Masa Karekod Üretici</span>
                </button>
              </div>
            </div>

            {/* Veri & SEO */}
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 mb-2 block">
                Veri & SEO
              </span>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveSection("analytics")}
                  className={getNavItemClass("analytics")}
                >
                  <TrendingUp className="size-4 shrink-0" />
                  <span>Menü Analitiği</span>
                </button>
                <button
                  onClick={() => setActiveSection("advanced")}
                  className={getNavItemClass("advanced")}
                >
                  <FileText className="size-4 shrink-0" />
                  <span>Gelişmiş & SEO</span>
                </button>
              </div>
            </div>
          </nav>

          {/* Configuration Form Card Window */}
          <div className="flex-1 p-6 overflow-y-auto max-w-3xl">
            {/* Section 1: Kanal Durumu */}
            {activeSection === "status" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-bold tracking-tight text-slate-900">
                    Kanal Durumu
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Dijital menünüzün yayın durumunu kontrol edin ve hızlıca erişin.
                  </p>
                </div>

                {/* Status Card */}
                <div className="border border-slate-200/80 rounded-2xl bg-white shadow-xs">
                  {/* Toggle + Status */}
                  <div className="flex items-center justify-between px-6 py-5">
                    <div className="flex items-center gap-4">
                      <div className="flex size-11 items-center justify-center rounded-xl bg-slate-50 text-slate-500 border border-slate-100">
                        <Smartphone className="size-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">QR Menü</span>
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide ${
                              isEnabled
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-slate-100 text-slate-550 border border-slate-200"
                            }`}
                          >
                            <span
                              className={`size-1.5 rounded-full ${isEnabled ? "bg-emerald-500" : "bg-slate-400"}`}
                            />
                            {isEnabled ? "Aktif" : "Pasif"}
                          </span>
                        </div>
                        <span className="block text-xs text-slate-500 mt-1 leading-normal">
                          {isEnabled
                            ? "Müşterileriniz masalardan menüyü görüntüleyebilir."
                            : "Menü şu anda müşterilere kapalı."}
                        </span>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => {
                          setIsEnabled(e.target.checked);
                          toast.success(
                            e.target.checked
                              ? "QR Menü aktif edildi."
                              : "QR Menü devre dışı bırakıldı.",
                          );
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5.5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:border-slate-350 after:border after:rounded-full after:h-4.5 after:w-4.5 after:transition-all peer-checked:bg-slate-900" />
                    </label>
                  </div>

                  <Separator className="bg-slate-100" />

                  {/* URL Section */}
                  <div className="px-6 py-5">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <span className="block text-xs font-semibold text-slate-700">
                          Menü Bağlantısı
                        </span>
                        <input
                          type="text"
                          readOnly
                          value={publicMenuUrl}
                          className="mt-2 w-full bg-slate-50 border border-slate-150 rounded-xl px-3.5 py-2.5 text-xs text-slate-600  focus:outline-none"
                        />
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          onClick={handleCopyLink}
                          variant="outline"
                          className="h-9 text-[11px] border-slate-200 text-slate-655 font-bold rounded-xl hover:bg-slate-50 shadow-2xs"
                        >
                          <Copy className="size-3.5 mr-1" /> Kopyala
                        </Button>
                        <a href={publicMenuUrl} target="_blank" rel="noreferrer">
                          <Button
                            variant="outline"
                            className="h-9 text-[11px] border-slate-200 text-slate-655 font-bold rounded-xl hover:bg-slate-50 shadow-2xs"
                          >
                            <ExternalLink className="size-3.5 mr-1" /> Aç
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-slate-100" />

                  {/* Info Row */}
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-4 bg-slate-50/40 text-xs text-slate-500 rounded-b-2xl">
                    <span>
                      Son güncelleme:{" "}
                      <strong className="font-semibold text-slate-700">{lastUpdated}</strong>
                    </span>
                    <span>
                      Tema:{" "}
                      <strong className="font-semibold text-slate-700">
                        {selectedThemeId === "custom" ? "Özel Palet" : activeTheme.name}
                      </strong>
                    </span>
                    <span>
                      Dil:{" "}
                      <strong className="font-semibold text-slate-700">
                        {availableLanguages.filter((l) => l.enabled).length}/
                        {availableLanguages.length}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Section 2: Menü Yapılandırması */}
            {activeSection === "menus" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-bold tracking-tight text-slate-900">
                        Menü Yapılandırması
                      </h2>
                      <ComingSoonBadge />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Alt menü zamanlaması QR menünüzde henüz desteklenmiyor. Menü içerikleri
                      şimdilik ürün kataloğundan otomatik alınır.
                    </p>
                  </div>
                  <Button
                    disabled
                    className="h-9 text-xs bg-slate-200 text-slate-500 font-semibold rounded-xl px-4 cursor-not-allowed"
                  >
                    <Plus className="size-3.5 mr-1" /> Menü Oluştur
                  </Button>
                </div>

                <div className="border border-slate-150 rounded-2xl bg-white shadow-xs overflow-hidden divide-y divide-slate-100 opacity-50 pointer-events-none select-none">
                  {menus.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between p-5 hover:bg-slate-50/30 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        {/* Drag handle */}
                        <div className="cursor-grab text-slate-350 hover:text-slate-400 p-1">
                          <span className="block w-4.5 h-0.5 bg-current mb-0.75 rounded-full" />
                          <span className="block w-4.5 h-0.5 bg-current mb-0.75 rounded-full" />
                          <span className="block w-4.5 h-0.5 bg-current rounded-full" />
                        </div>

                        <div>
                          <div className="flex items-center gap-2.5">
                            <span className="text-sm font-bold text-slate-950">{m.name}</span>
                            <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-bold">
                              {m.categoryCount} Kategori
                            </span>
                          </div>
                          <span className="text-xs text-slate-400 flex items-center gap-1.5 mt-1 font-semibold">
                            <Clock className="size-3.5 text-slate-400" /> {m.schedule}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5">
                        <button
                          onClick={() => {
                            setMenus(
                              menus.map((item) =>
                                item.id === m.id ? { ...item, visible: !item.visible } : item,
                              ),
                            );
                            toast.success(
                              m.visible ? `${m.name} gizlendi.` : `${m.name} gösterildi.`,
                            );
                          }}
                          className={`p-2 rounded-xl border transition-all ${
                            m.visible
                              ? "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100"
                              : "bg-slate-50 text-slate-400 border-slate-150 hover:bg-slate-100"
                          }`}
                          title={m.visible ? "Menüyü Gizle" : "Menüyü Göster"}
                        >
                          {m.visible ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                        </button>

                        <button
                          onClick={() => {
                            const sched = prompt(
                              "Bu menü için geçerli saat/takvim programını yazın:",
                              m.schedule,
                            );
                            if (sched !== null) {
                              setMenus(
                                menus.map((item) =>
                                  item.id === m.id ? { ...item, schedule: sched } : item,
                                ),
                              );
                              toast.success("Çalışma saatleri güncellendi.");
                            }
                          }}
                          className="p-2 rounded-xl border border-slate-200 text-slate-655 hover:bg-slate-50 text-xs flex items-center gap-1.5 font-bold shadow-2xs"
                        >
                          <Calendar className="size-4" /> Saatler
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section 3: Hazır Tasarım Temaları */}
            {activeSection === "themes" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-bold tracking-tight text-slate-900">
                    Hazır Tasarım Temaları
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Dijital menünüzde kullanılan tasarım teması. Şu an tek tema mevcuttur; yeni
                    temalar yakında eklenecek.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {THEME_PRESETS.map((theme) => {
                    const isSelected = selectedThemeId === theme.id;
                    return (
                      <div
                        key={theme.id}
                        onClick={() => handleApplyPresetTheme(theme)}
                        className={`cursor-pointer rounded-2xl border p-4 bg-white transition-all duration-200 ${
                          isSelected
                            ? "border-slate-900 ring-1 ring-slate-900 shadow-sm"
                            : "border-slate-200/80 shadow-xs hover:border-slate-350"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-bold text-slate-900">{theme.name}</span>
                          {isSelected && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-900 px-2 py-0.5 text-[9px] font-bold text-white">
                              <Check className="size-2.5" /> Seçili
                            </span>
                          )}
                        </div>

                        {/* /menu tasarımının mini temsili */}
                        <div className="h-28 w-full rounded-xl mb-3 relative overflow-hidden border border-slate-100 bg-white">
                          <div className="h-10 w-full bg-gradient-to-r from-zinc-200 via-zinc-100 to-amber-100/60" />
                          <div className="absolute top-6 left-1/2 -translate-x-1/2 size-8 rounded-full bg-white border-2 border-white shadow-md flex items-center justify-center">
                            <span
                              className="size-4 rounded-full"
                              style={{ backgroundColor: theme.primary }}
                            />
                          </div>
                          <div className="pt-6 flex flex-col items-center gap-1">
                            <span className="h-1.5 w-16 rounded-full bg-zinc-800" />
                            <span className="h-1 w-8 rounded-full bg-amber-500" />
                          </div>
                          <div className="absolute bottom-2 left-3 right-3 grid grid-cols-2 gap-1.5">
                            <span className="h-5 rounded-lg bg-amber-50 border border-amber-100" />
                            <span className="h-5 rounded-lg bg-emerald-50 border border-emerald-100" />
                          </div>
                        </div>

                        <p className="text-[10px] text-slate-500 leading-relaxed">
                          Canlı QR menünüzde kullanılan imza tasarım: beyaz zemin, amber vurgular ve
                          kart bazlı kategori ızgarası.
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-start gap-2.5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                  <Info className="size-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    Yeni tasarım temaları üzerinde çalışıyoruz. Yayınlandıklarında bu listeden tek
                    tıkla geçiş yapabileceksiniz.
                  </p>
                </div>
              </div>
            )}

            {/* Section 4: Görsel Arayüz (Appearance) */}
            {activeSection === "appearance" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-bold tracking-tight text-slate-900">
                    Görsel Arayüz (Appearance)
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Logo, renkler, buton kenarlıkları ve sayfa düzenlerini detaylıca özelleştirin.
                  </p>
                </div>

                {/* Cover & Logo Editor */}
                <div className="border border-slate-150 rounded-2xl p-6 bg-white shadow-xs space-y-5">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Logo & Görsel Yükle
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <span className="block text-xs font-semibold text-slate-700">
                        Logo Görseli
                      </span>
                      <div className="flex items-center gap-4">
                        <img
                          src={logoUrl}
                          alt="Logo"
                          className="size-14 rounded-2xl object-cover border border-slate-150/60 shadow-xs"
                        />
                        <input
                          type="file"
                          ref={logoInputRef}
                          onChange={handleLogoUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        <Button
                          onClick={() => logoInputRef.current?.click()}
                          variant="outline"
                          className="h-9 text-[11px] border-slate-255 font-bold rounded-xl shadow-2xs hover:bg-slate-50"
                        >
                          <Upload className="size-3.5 mr-1" /> Değiştir
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <span className="block text-xs font-semibold text-slate-700">
                        Kapak Fotoğrafı
                      </span>
                      <div className="flex items-center gap-4">
                        <img
                          src={coverImageUrl}
                          alt="Kapak"
                          className="w-20 h-14 rounded-2xl object-cover border border-slate-150/60 shadow-xs"
                        />
                        <input
                          type="file"
                          ref={coverInputRef}
                          onChange={handleCoverUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        <Button
                          onClick={() => coverInputRef.current?.click()}
                          variant="outline"
                          className="h-9 text-[11px] border-slate-255 font-bold rounded-xl shadow-2xs hover:bg-slate-50"
                        >
                          <Upload className="size-3.5 mr-1" /> Değiştir
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Colors & Palette Editor */}
                <div className="border border-slate-150 rounded-2xl p-6 bg-white shadow-xs space-y-5">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Renk Paleti
                  </span>
                  <p className="text-[10px] text-slate-450 -mt-3">
                    Vurgu, başlık ve arka plan renkleri kaydettiğinizde QR menünüze anında yansır.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        Vurgu Rengi (Ana)
                      </label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={(e) => {
                            setPrimaryColor(e.target.value);
                            setSelectedThemeId("custom");
                          }}
                          className="w-10 h-10 rounded-xl cursor-pointer border border-slate-255 p-0 overflow-hidden"
                        />
                        <input
                          type="text"
                          value={primaryColor}
                          onChange={(e) => {
                            setPrimaryColor(e.target.value);
                            setSelectedThemeId("custom");
                          }}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2.5  focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        Başlık Rengi (İkincil)
                      </label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={secondaryColor}
                          onChange={(e) => {
                            setSecondaryColor(e.target.value);
                            setSelectedThemeId("custom");
                          }}
                          className="w-10 h-10 rounded-xl cursor-pointer border border-slate-255 p-0 overflow-hidden"
                        />
                        <input
                          type="text"
                          value={secondaryColor}
                          onChange={(e) => {
                            setSecondaryColor(e.target.value);
                            setSelectedThemeId("custom");
                          }}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2.5  focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        Arka Plan
                      </label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={backgroundColor}
                          onChange={(e) => {
                            setBackgroundColor(e.target.value);
                            setSelectedThemeId("custom");
                          }}
                          className="w-10 h-10 rounded-xl cursor-pointer border border-slate-255 p-0 overflow-hidden"
                        />
                        <input
                          type="text"
                          value={backgroundColor}
                          onChange={(e) => {
                            setBackgroundColor(e.target.value);
                            setSelectedThemeId("custom");
                          }}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2.5  focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Typography and Layout Details */}
                <div className="border border-slate-150 rounded-2xl p-6 bg-white shadow-xs space-y-5">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Biçimlendirme & Düzen
                  </span>
                  <p className="text-[10px] text-slate-450 -mt-3">
                    Yazı tipi, köşe yuvarlama, buton ve kart stilleri kaydettiğinizde QR menünüze
                    anında yansır.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        Yazı Tipi (Font)
                      </label>
                      <select
                        value={fontFamily}
                        onChange={(e) => {
                          setFontFamily(e.target.value);
                          setSelectedThemeId("custom");
                        }}
                        className="w-full text-xs border border-slate-255 rounded-xl p-3 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white shadow-2xs font-semibold"
                      >
                        {QR_FONT_OPTIONS.map((font) => (
                          <option key={font.id} value={font.id} style={{ fontFamily: font.stack }}>
                            {font.name}
                          </option>
                        ))}
                      </select>
                      <p
                        className="text-[10px] text-slate-450 leading-relaxed"
                        style={{ fontFamily: getQrFontStack(fontFamily) }}
                      >
                        Örnek: Nitelikli Kahveler — Espresso ₺110
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        Köşe Yuvarlama (Radius)
                      </label>
                      <div className="flex items-center gap-4 border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50/30">
                        <input
                          type="range"
                          min="0"
                          max="36"
                          value={borderRadius}
                          onChange={(e) => {
                            setBorderRadius(Number(e.target.value));
                            setSelectedThemeId("custom");
                          }}
                          className="flex-1 accent-slate-955 h-1.5 bg-slate-250 rounded-lg cursor-pointer"
                        />
                        <span className="text-xs font-extrabold text-slate-700 w-10 text-right">
                          {borderRadius}px
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        Buton Kenarlık Stili
                      </label>
                      <div className="grid grid-cols-3 gap-1 bg-slate-55/60 p-1 rounded-xl border border-slate-200">
                        {(["sharp", "rounded", "pill"] as const).map((style) => (
                          <button
                            key={style}
                            onClick={() => {
                              setButtonStyle(style);
                              setSelectedThemeId("custom");
                            }}
                            className={`py-2 text-[10px] font-extrabold rounded-lg capitalize transition-all ${
                              buttonStyle === style
                                ? "bg-white text-slate-955 shadow-2xs border border-slate-100"
                                : "text-slate-550 hover:text-slate-800"
                            }`}
                          >
                            {style === "sharp"
                              ? "Köşeli"
                              : style === "rounded"
                                ? "Oval"
                                : "Tam Yuvarlak"}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-slate-700">
                        Ürün Kart Stili
                      </label>
                      <div className="grid grid-cols-3 gap-1 bg-slate-55/60 p-1 rounded-xl border border-slate-200">
                        {(["flat", "elevated", "glass"] as const).map((style) => (
                          <button
                            key={style}
                            onClick={() => {
                              setCardStyle(style);
                              setSelectedThemeId("custom");
                            }}
                            className={`py-2 text-[10px] font-extrabold rounded-lg capitalize transition-all ${
                              cardStyle === style
                                ? "bg-white text-slate-955 shadow-2xs border border-slate-100"
                                : "text-slate-550 hover:text-slate-800"
                            }`}
                          >
                            {style === "flat"
                              ? "Düz"
                              : style === "elevated"
                                ? "Gölge"
                                : "Buzlu Cam"}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature Toggles */}
                <div className="border border-slate-150 rounded-2xl p-6 bg-white shadow-xs space-y-5">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Özellikler
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    <label className="flex items-center justify-between cursor-pointer group p-3 rounded-xl hover:bg-slate-50/50 transition-all border border-transparent hover:border-slate-100">
                      <div>
                        <span className="block text-xs font-bold text-slate-900 group-hover:text-slate-955">
                          Arama Çubuğu
                        </span>
                        <span className="block text-[10px] text-slate-450 mt-0.5">
                          Menü içi anında arama
                        </span>
                      </div>
                      <Switch
                        checked={showSearchBar}
                        onCheckedChange={(val: boolean) => setShowSearchBar(val)}
                      />
                    </label>

                    <label className="flex items-center justify-between cursor-pointer group p-3 rounded-xl hover:bg-slate-50/50 transition-all border border-transparent hover:border-slate-100">
                      <div>
                        <span className="block text-xs font-bold text-slate-900 group-hover:text-slate-955">
                          Kategori İkonları
                        </span>
                        <span className="block text-[10px] text-slate-450 mt-0.5">
                          Kategori fotoğrafları üzerinde ikon rozeti
                        </span>
                      </div>
                      <Switch
                        checked={showCategoryIcons}
                        onCheckedChange={(val: boolean) => setShowCategoryIcons(val)}
                      />
                    </label>

                    <label className="flex items-center justify-between cursor-pointer group p-3 rounded-xl hover:bg-slate-50/50 transition-all border border-transparent hover:border-slate-100">
                      <div>
                        <span className="block text-xs font-bold text-slate-900 group-hover:text-slate-955">
                          Şefin Favorileri
                        </span>
                        <span className="block text-[10px] text-slate-450 mt-0.5">
                          Ana sayfada favoriler kartı gösterilir
                        </span>
                      </div>
                      <Switch
                        checked={showFeaturedProducts}
                        onCheckedChange={(val: boolean) => setShowFeaturedProducts(val)}
                      />
                    </label>

                    <label className="flex items-center justify-between p-3 rounded-xl border border-transparent opacity-50 cursor-not-allowed">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="block text-xs font-bold text-slate-900">
                            Yapışkan Kategoriler
                          </span>
                          <ComingSoonBadge />
                        </div>
                        <span className="block text-[10px] text-slate-450 mt-0.5">
                          Kaydırırken kategoriler üstte sabitlenir
                        </span>
                      </div>
                      <Switch
                        checked={stickyCategories}
                        disabled
                        onCheckedChange={(val: boolean) => setStickyCategories(val)}
                      />
                    </label>

                    <label className="flex items-center justify-between cursor-pointer group p-3 rounded-xl hover:bg-slate-50/50 transition-all border border-transparent hover:border-slate-100">
                      <div>
                        <span className="block text-xs font-bold text-slate-900 group-hover:text-slate-955">
                          Geçiş Animasyonları
                        </span>
                        <span className="block text-[10px] text-slate-450 mt-0.5">
                          Yumuşak hover ve scroll animasyonları
                        </span>
                      </div>
                      <Switch
                        checked={enableAnimations}
                        onCheckedChange={(val: boolean) => setEnableAnimations(val)}
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Section 5: İşletme Bilgileri */}
            {activeSection === "business" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-bold tracking-tight text-slate-900">
                    İşletme Bilgileri
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Müşterilerinizin menü altında göreceği iletişim ve adres bilgilerini girin.
                  </p>
                </div>

                <div className="border border-slate-150 rounded-2xl bg-white shadow-xs overflow-hidden">
                  {/* İletişim Bilgileri */}
                  <div className="p-6 space-y-5">
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2.5">
                      <Phone className="size-3.5" /> İletişim Bilgileri
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          İşletme Adı
                        </label>
                        <input
                          type="text"
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          Çalışma Saatleri
                        </label>
                        <input
                          type="text"
                          value={workingHours}
                          onChange={(e) => setWorkingHours(e.target.value)}
                          placeholder="Örn: Her gün 08:00 - 23:30"
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          İşletme Açıklaması
                        </label>
                        <textarea
                          rows={3}
                          value={businessDescription}
                          onChange={(e) => setBusinessDescription(e.target.value)}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white leading-relaxed"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          Telefon
                        </label>
                        <input
                          type="text"
                          value={businessPhone}
                          onChange={(e) => setBusinessPhone(e.target.value)}
                          placeholder="+90 212 555 1234"
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          WhatsApp Numarası
                        </label>
                        <input
                          type="text"
                          value={businessWhatsApp}
                          onChange={(e) => setBusinessWhatsApp(e.target.value)}
                          placeholder="+90 532 555 5678"
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">Adres</label>
                        <input
                          type="text"
                          value={businessAddress}
                          onChange={(e) => setBusinessAddress(e.target.value)}
                          placeholder="Mahalle, cadde, no, ilçe, il"
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-slate-100" />

                  {/* Sosyal Medya */}
                  <div className="p-6 space-y-5">
                    <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2.5">
                      <Globe className="size-3.5" /> Sosyal Medya & Bağlantılar
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          Instagram Hesabı
                        </label>
                        <input
                          type="text"
                          value={businessInstagram}
                          onChange={(e) => setBusinessInstagram(e.target.value)}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          Facebook Sayfası
                        </label>
                        <input
                          type="text"
                          value={businessFacebook}
                          onChange={(e) => setBusinessFacebook(e.target.value)}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          Google Maps Konum Linki
                        </label>
                        <input
                          type="text"
                          value={businessGmaps}
                          onChange={(e) => setBusinessGmaps(e.target.value)}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-700">
                          Web Sitesi
                        </label>
                        <input
                          type="text"
                          value={businessWebsite}
                          onChange={(e) => setBusinessWebsite(e.target.value)}
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 6: Çoklu Dil & Çeviri */}
            {activeSection === "languages" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-bold tracking-tight text-slate-900">
                    Çoklu Dil & Çeviri
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Müşterilerinize sunduğunuz dil seçeneklerini ve otomatik çevirileri yönetin.
                  </p>
                </div>

                <div className="border border-slate-150 rounded-2xl p-6 bg-white shadow-xs space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="block text-sm font-bold text-slate-900">
                        Çoklu Dil Desteği
                      </span>
                      <span className="block text-xs text-slate-400 mt-0.5">
                        Müşteriler diller arasında geçiş yapabilir.
                      </span>
                    </div>
                    <Switch
                      checked={isMultiLang}
                      onCheckedChange={(val: boolean) => setIsMultiLang(val)}
                    />
                  </div>

                  <div className="h-px bg-slate-100" />

                  {isMultiLang && (
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                          Varsayılan Dil
                        </label>
                        <select
                          value={defaultLanguage}
                          onChange={(e) => setDefaultLanguage(e.target.value)}
                          className="w-full text-xs border border-slate-200 rounded-xl p-3 bg-white focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 font-semibold"
                        >
                          <option value="tr">Türkçe</option>
                          <option value="en">English</option>
                        </select>
                      </div>

                      <div className="space-y-3">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                          Aktif Dil Seçenekleri
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          {availableLanguages.map((lang) => {
                            const isSupported = lang.code === "tr" || lang.code === "en";
                            return (
                              <label
                                key={lang.code}
                                className={`flex items-center gap-3 p-3 rounded-xl border border-slate-150 text-xs transition-all ${
                                  isSupported
                                    ? "hover:bg-slate-50 cursor-pointer"
                                    : "opacity-50 cursor-not-allowed bg-slate-50/50"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSupported && lang.enabled}
                                  disabled={!isSupported}
                                  onChange={(e) => {
                                    setAvailableLanguages(
                                      availableLanguages.map((l) =>
                                        l.code === lang.code
                                          ? { ...l, enabled: e.target.checked }
                                          : l,
                                      ),
                                    );
                                  }}
                                  className="rounded border-slate-350 text-slate-900 size-4 focus:ring-slate-955 focus:ring-offset-0 cursor-pointer disabled:cursor-not-allowed"
                                />
                                <span className="font-bold text-slate-700 flex-1">{lang.name}</span>
                                {!isSupported && (
                                  <span className="text-[8px] font-black uppercase tracking-wider text-slate-400">
                                    Yakında
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div className="pt-2">
                        <Button
                          disabled
                          className="w-full h-11 text-xs bg-slate-200 text-slate-500 font-semibold flex items-center justify-center gap-2 rounded-xl cursor-not-allowed"
                        >
                          <Sparkles className="size-4" />
                          Yapay Zeka (AI) ile Tümünü Çevir
                        </Button>
                        <span className="block text-[10px] text-slate-400 text-center mt-2.5 leading-normal">
                          Otomatik AI çevirisi yakında aktif olacak. Şimdilik Türkçe ve İngilizce
                          çeviriler hazır olarak sunulur.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section 7: Tanıtım & Duyuru Afişleri */}
            {activeSection === "banners" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-bold tracking-tight text-slate-900">
                      Tanıtım & Duyuru Afişleri
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                      Ana sayfada kayan afişler olarak gösterilir. Yalnızca görünür olanlar menüde
                      yayınlanır.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowNewBannerForm(true)}
                    className="h-9 text-xs bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl px-4 shadow-xs"
                  >
                    <Plus className="size-3.5 mr-1" /> Yeni Afiş Ekle
                  </Button>
                </div>

                {showNewBannerForm && (
                  <form
                    onSubmit={handleAddBanner}
                    className="border border-slate-200 rounded-2xl p-5 bg-slate-50/30 shadow-inner space-y-4"
                  >
                    <div className="flex items-center justify-between border-b border-slate-150 pb-3">
                      <span className="text-xs font-bold text-slate-800">Yeni Afiş Bilgileri</span>
                      <button type="button" onClick={() => setShowNewBannerForm(false)}>
                        <Trash2 className="size-4 text-slate-450 hover:text-red-500 transition-colors" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          Afiş Başlığı
                        </label>
                        <input
                          type="text"
                          value={newBannerTitle}
                          onChange={(e) => setNewBannerTitle(e.target.value)}
                          placeholder="Örn: Hafta Sonu Özel Kokteyller"
                          required
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 bg-white focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          Tarih Aralığı
                        </label>
                        <input
                          type="text"
                          value={newBannerDates}
                          onChange={(e) => setNewBannerDates(e.target.value)}
                          placeholder="Örn: 1-15 Temmuz veya Her gün"
                          className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 bg-white focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        type="button"
                        onClick={() => setShowNewBannerForm(false)}
                        variant="outline"
                        className="h-8.5 text-[11px] font-bold rounded-xl"
                      >
                        Vazgeç
                      </Button>
                      <Button
                        type="submit"
                        className="h-8.5 text-[11px] font-bold bg-slate-900 text-white hover:bg-slate-800 rounded-xl px-3"
                      >
                        Kaydet ve Yayınla
                      </Button>
                    </div>
                  </form>
                )}

                <div className="border border-slate-150 rounded-2xl bg-white shadow-xs overflow-hidden divide-y divide-slate-100">
                  {banners.map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center justify-between p-5 hover:bg-slate-50/30 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <img
                          src={b.image}
                          alt={b.title}
                          className="w-18 h-12 object-cover rounded-xl border border-slate-150 shadow-xs"
                        />
                        <div>
                          <span className="block text-xs font-bold text-slate-900">{b.title}</span>
                          <span className="block text-[10px] text-slate-450 mt-1 font-semibold">
                            Tarih: {b.dateRange}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setBanners(
                              banners.map((item) =>
                                item.id === b.id ? { ...item, visible: !item.visible } : item,
                              ),
                            );
                            toast.success(
                              b.visible ? "Afiş yayından kaldırıldı." : "Afiş yayına alındı.",
                            );
                          }}
                          className={`p-2 rounded-xl border transition-all ${
                            b.visible
                              ? "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100"
                              : "bg-slate-50 text-slate-400 border-slate-150 hover:bg-slate-100"
                          }`}
                        >
                          {b.visible ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                        </button>

                        <button
                          onClick={() => {
                            setBanners(banners.filter((item) => item.id !== b.id));
                            toast.success("Afiş silindi.");
                          }}
                          className="p-2 rounded-xl border border-slate-200 text-red-500 hover:bg-red-50 hover:border-red-100 transition-all"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section 8: QR Kod Üretici */}
            {activeSection === "qrcodes" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-bold tracking-tight text-slate-900">
                    Masa Karekod Üretici
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Masalarınız için özel tasarlanmış QR menü karekodlarını oluşturun.
                  </p>
                </div>

                <div className="border border-slate-150 rounded-2xl p-6 bg-white shadow-xs space-y-5">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Hızlı QR İndir
                  </span>
                  <div className="flex flex-col sm:flex-row items-center gap-4 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
                    <div className="bg-white p-2 rounded-xl border border-slate-150/60 shadow-2xs">
                      {renderMockQRCode("GENEL")}
                    </div>
                    <div className="flex-1 space-y-1 text-center sm:text-left">
                      <span className="block text-xs font-bold text-slate-900">
                        Genel Menü QR Kodu
                      </span>
                      <span className="block text-[10px] text-slate-550 leading-relaxed">
                        Herhangi bir masa numarası içermez, doğrudan ana dijital menüye yönlendirir.
                      </span>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        onClick={() => toast.success("PNG formatında QR indirildi.")}
                        className="h-9 text-[11px] font-bold bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 px-3.5 rounded-xl shadow-2xs"
                      >
                        <Download className="size-3.5 mr-1 inline-block" /> PNG
                      </Button>
                      <Button
                        onClick={() => toast.success("SVG formatında QR indirildi.")}
                        className="h-9 text-[11px] font-bold bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 px-3.5 rounded-xl shadow-2xs"
                      >
                        <Download className="size-3.5 mr-1 inline-block" /> SVG
                      </Button>
                    </div>
                  </div>

                  <div className="h-px bg-slate-100" />

                  {/* Mass generating block */}
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                        Masa Bazlı QR Listesi
                      </span>
                      <form onSubmit={handleAddTable} className="flex gap-2">
                        <input
                          type="text"
                          value={newTableNumber}
                          onChange={(e) => setNewTableNumber(e.target.value)}
                          placeholder="Masa Adı / No (örn: Masa 9)"
                          className="text-xs border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                        />
                        <Button
                          type="submit"
                          className="h-9 text-[11px] bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 rounded-xl shadow-xs"
                        >
                          <Plus className="size-3.5 mr-1 inline-block" /> Ekle
                        </Button>
                      </form>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {tableQRList.map((t) => (
                        <div
                          key={t.tableNumber}
                          className="border border-slate-150/60 rounded-2xl p-4 bg-white hover:border-slate-350 hover:shadow-xs transition-all flex flex-col items-center text-center relative group"
                        >
                          <button
                            type="button"
                            onClick={() => handleRemoveTable(t.tableNumber)}
                            className="absolute top-2.5 right-2.5 text-slate-350 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all p-1 hover:bg-slate-50 rounded-lg"
                          >
                            <Trash2 className="size-3.5" />
                          </button>

                          <div className="mb-3 bg-slate-50/50 p-2.5 rounded-2xl border border-slate-100 shadow-2xs">
                            {renderMockQRCode(t.code)}
                          </div>
                          <span className="text-xs font-bold text-slate-855">{t.tableNumber}</span>
                          <span className="text-[9px] text-slate-400  mt-0.5">{t.code}</span>

                          <div className="flex gap-1.5 mt-4 w-full">
                            <button
                              onClick={() =>
                                toast.success(`${t.tableNumber} QR kodu yazdırılıyor...`)
                              }
                              className="flex-1 py-1.5 text-[9px] font-bold border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-655 flex items-center justify-center gap-1.5 shadow-2xs"
                            >
                              <Printer className="size-3" /> Yazdır
                            </button>
                            <button
                              onClick={() => toast.success(`${t.tableNumber} PDF indirildi.`)}
                              className="px-2 py-1.5 text-[9px] font-bold border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-655 shadow-2xs"
                              title="PDF İndir"
                            >
                              <Download className="size-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="pt-2">
                      <Button
                        onClick={() =>
                          toast.success(
                            "Tüm masa QR kodları tek bir PDF olarak hazırlandı. İndirme başlatılıyor...",
                          )
                        }
                        className="w-full h-11 text-xs bg-slate-900 hover:bg-slate-800 text-white font-semibold flex items-center justify-center gap-2 shadow-xs rounded-xl transition-all"
                      >
                        <Printer className="size-4" /> Toplu QR Çıktısı Al (PDF)
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 9: Analitik */}
            {activeSection === "analytics" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-bold tracking-tight text-slate-900">
                    QR Menü Analitiği
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Müşterilerinizin menü etkileşimlerini, görüntüleme sayılarını ve popüler
                    ürünleri inceleyin.
                  </p>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="border border-slate-150 p-5 rounded-2xl bg-white shadow-xs space-y-1.5 hover:shadow-sm transition-shadow">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Bugünkü Görüntüleme
                    </span>
                    <span className="block text-2xl font-black text-slate-900">842</span>
                    <span className="block text-[10px] text-emerald-600 font-extrabold flex items-center gap-0.5">
                      ▲ %12 <span className="text-slate-400 font-medium">(Düne göre)</span>
                    </span>
                  </div>

                  <div className="border border-slate-150 p-5 rounded-2xl bg-white shadow-xs space-y-1.5 hover:shadow-sm transition-shadow">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Haftalık Trafik
                    </span>
                    <span className="block text-2xl font-black text-slate-900">5,830</span>
                    <span className="block text-[10px] text-emerald-600 font-extrabold flex items-center gap-0.5">
                      ▲ %4.2 <span className="text-slate-400 font-medium">(Geçen haftaya)</span>
                    </span>
                  </div>

                  <div className="border border-slate-150 p-5 rounded-2xl bg-white shadow-xs space-y-1.5 hover:shadow-sm transition-shadow">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      En Çok Okutulan QR
                    </span>
                    <span className="block text-2xl font-black text-slate-900">Masa 4</span>
                    <span className="block text-[10px] text-slate-400 font-bold">
                      Toplam 240 okutma
                    </span>
                  </div>

                  <div className="border border-slate-150 p-5 rounded-2xl bg-white shadow-xs space-y-1.5 hover:shadow-sm transition-shadow">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      En Çok İncelenen Ürün
                    </span>
                    <span className="block text-lg font-extrabold text-slate-900 truncate">
                      Chai Tea Latte
                    </span>
                    <span className="block text-[10px] text-slate-450 font-bold">142 tıklama</span>
                  </div>

                  <div className="border border-slate-150 p-5 rounded-2xl bg-white shadow-xs space-y-1.5 hover:shadow-sm transition-shadow">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      En Çok Gezilen Kategori
                    </span>
                    <span className="block text-lg font-extrabold text-slate-900 truncate">
                      Tatlı & Fırın
                    </span>
                    <span className="block text-[10px] text-slate-450 font-bold">
                      %38 oranla en popüler
                    </span>
                  </div>

                  <div className="border border-slate-150 p-5 rounded-2xl bg-white shadow-xs space-y-1.5 hover:shadow-sm transition-shadow">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Ort. Oturum Süresi
                    </span>
                    <span className="block text-2xl font-black text-slate-900">4.8 dk</span>
                    <span className="block text-[10px] text-slate-400 font-bold">
                      Masa başında ortalama süre
                    </span>
                  </div>
                </div>

                {/* SVG Chart */}
                <div className="border border-slate-100 rounded-2xl p-6 bg-white shadow-xs space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="block text-xs font-bold text-slate-900">
                        Saatlik Trafik Dağılımı
                      </span>
                      <span className="block text-[10px] text-slate-450 mt-0.5">
                        Gün içi en yoğun saatlerin analizi
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-slate-700 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-150">
                      Son 24 Saat
                    </span>
                  </div>

                  {/* Sparkline chart bar */}
                  <div className="h-36 flex items-end gap-2 pt-6">
                    {[
                      12, 18, 15, 25, 45, 60, 85, 95, 70, 50, 40, 55, 75, 90, 110, 120, 105, 80, 65,
                      45, 30, 20, 10, 8,
                    ].map((val, idx) => (
                      <div
                        key={idx}
                        className="flex-1 flex flex-col items-center group relative cursor-pointer"
                      >
                        <div
                          style={{ height: `${(val / 120) * 100}%` }}
                          className="w-full bg-slate-100 group-hover:bg-slate-950 rounded-t-md transition-all duration-300 relative shadow-2xs"
                        />
                        <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-slate-950 text-white text-[9px] px-2 py-0.75 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-md z-30 font-bold">
                          {val} Ziyaret
                        </span>
                        <span className="text-[8px] text-slate-400 mt-2  font-semibold">
                          {idx === 0
                            ? "08"
                            : idx === 6
                              ? "11"
                              : idx === 12
                                ? "14"
                                : idx === 18
                                  ? "18"
                                  : idx === 23
                                    ? "23"
                                    : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section 10: Gelişmiş & SEO Ayarları */}
            {activeSection === "advanced" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-bold tracking-tight text-slate-900">
                    Gelişmiş & SEO Ayarları
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Fiyat görünürlüğü, alerjen uyarıları ve arama motoru optimizasyonu.
                  </p>
                </div>

                <div className="border border-slate-150 rounded-2xl p-6 bg-white shadow-xs space-y-5">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Menü Detay Filtreleri
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    <label className="flex items-center justify-between cursor-pointer group p-3 rounded-xl hover:bg-slate-50/50 transition-all border border-transparent hover:border-slate-100">
                      <div>
                        <span className="block text-xs font-bold text-slate-900 group-hover:text-slate-955">
                          Fiyatları Gizle
                        </span>
                        <span className="block text-[10px] text-slate-455 mt-0.5">
                          Ürün fiyatları menüde görünmez
                        </span>
                      </div>
                      <Switch
                        checked={hidePrices}
                        onCheckedChange={(val: boolean) => setHidePrices(val)}
                      />
                    </label>

                    <label className="flex items-center justify-between cursor-pointer group p-3 rounded-xl hover:bg-slate-50/50 transition-all border border-transparent hover:border-slate-100">
                      <div>
                        <span className="block text-xs font-bold text-slate-900 group-hover:text-slate-955">
                          Tükenenleri Gizle
                        </span>
                        <span className="block text-[10px] text-slate-455 mt-0.5">
                          Stokta olmayan ürünleri sakla
                        </span>
                      </div>
                      <Switch
                        checked={hideUnavailable}
                        onCheckedChange={(val: boolean) => setHideUnavailable(val)}
                      />
                    </label>

                    <label className="flex items-center justify-between cursor-pointer group p-3 rounded-xl hover:bg-slate-50/50 transition-all border border-transparent hover:border-slate-100">
                      <div>
                        <span className="block text-xs font-bold text-slate-900 group-hover:text-slate-955">
                          Alerjenleri Göster
                        </span>
                        <span className="block text-[10px] text-slate-455 mt-0.5">
                          Gluten, süt vb. uyarı etiketleri
                        </span>
                      </div>
                      <Switch
                        checked={showAllergens}
                        onCheckedChange={(val: boolean) => setShowAllergens(val)}
                      />
                    </label>

                    <label className="flex items-center justify-between cursor-pointer group p-3 rounded-xl hover:bg-slate-50/50 transition-all border border-transparent hover:border-slate-100">
                      <div>
                        <span className="block text-xs font-bold text-slate-900 group-hover:text-slate-955">
                          Kalorileri Göster
                        </span>
                        <span className="block text-[10px] text-slate-455 mt-0.5">
                          Ürün kalori bilgilerini listele
                        </span>
                      </div>
                      <Switch
                        checked={showCalories}
                        onCheckedChange={(val: boolean) => setShowCalories(val)}
                      />
                    </label>
                  </div>
                </div>

                <div className="border border-slate-150 rounded-2xl p-6 bg-white shadow-xs space-y-5">
                  <span className="block text-xs font-bold text-slate-400 uppercase tracking-widest">
                    SEO & Sosyal Paylaşım
                  </span>

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-slate-700">
                        Arama Motoru Başlığı (Meta Title)
                      </label>
                      <input
                        type="text"
                        value={seoTitle}
                        onChange={(e) => setSeoTitle(e.target.value)}
                        className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-slate-700">
                        Meta Açıklaması (Meta Description)
                      </label>
                      <textarea
                        rows={3}
                        value={seoDescription}
                        onChange={(e) => setSeoDescription(e.target.value)}
                        className="w-full text-xs border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-slate-950 focus:ring-1 focus:ring-slate-950 bg-white leading-relaxed"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-slate-700">
                        Sosyal Paylaşım Görseli (Open Graph)
                      </label>
                      <div className="border border-dashed border-slate-200 hover:border-slate-400 rounded-xl p-5 text-center cursor-pointer hover:bg-slate-50/50 transition-all bg-white">
                        <ImageIcon className="size-7 text-slate-400 mx-auto mb-2" />
                        <span className="block text-xs font-bold text-slate-750">
                          Görsel Seç veya Sürükle
                        </span>
                        <span className="block text-[10px] text-slate-450 mt-1">
                          Paylaşılan linklerde görünecek kapak resmi
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side: iPhone Live Preview Device Frame */}
        <div className="w-[380px] border-l border-slate-200 bg-slate-50/30 p-5 hidden xl:flex flex-col items-center justify-start overflow-y-auto shrink-0 select-none">
          <div className="sticky top-0 flex flex-col items-center w-full">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <Smartphone className="size-3.5" /> Anlık Mobil Önizleme
            </span>

            {/* Custom Preview Tabs */}
            <div className="grid grid-cols-2 gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 mb-4 w-[260px]">
              <button
                onClick={() => setPreviewTab("kapak")}
                className={`py-1.5 text-xs font-bold rounded-lg transition-all ${
                  previewTab === "kapak"
                    ? "bg-white text-slate-950 shadow-2xs border border-slate-150"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Ana Sayfa
              </button>
              <button
                onClick={() => setPreviewTab("menu")}
                className={`py-1.5 text-xs font-bold rounded-lg transition-all ${
                  previewTab === "menu"
                    ? "bg-white text-slate-950 shadow-2xs border border-slate-150"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Ürün Listesi
              </button>
            </div>

            {/* iPhone Shell - Beautiful Premium White/Silver design */}
            <div className="w-[310px] h-[640px] rounded-[48px] bg-white border-[8px] border-slate-200 ring-8 ring-slate-100/50 shadow-2xl relative flex flex-col overflow-hidden shrink-0">
              {/* iPhone Notch & Status Bar */}
              <div className="absolute top-0 left-0 right-0 h-8 flex items-center justify-between px-6 z-50">
                <span className="text-[9px] font-black font-sans text-slate-900">09:41</span>
                {/* Dynamic Island pill */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-black rounded-full shadow-inner z-50" />
                <div className="flex items-center gap-1 text-[9px] font-black text-slate-900">
                  <span>5G</span>
                  <Wifi className="size-2.5" />
                  <div className="w-3.5 h-2 border rounded-xs p-[1px] flex items-center border-slate-900/60">
                    <div className="h-full w-2 rounded-2xs bg-slate-950" />
                  </div>
                </div>
              </div>

              {/* iPhone Screen Area — canlı /menu tasarımının mini kopyası */}
              <div
                className="flex-1 overflow-y-auto pt-8 relative flex flex-col"
                style={{ backgroundColor, color: secondaryColor, fontFamily: previewFontStack }}
              >
                {!isEnabled ? (
                  /* Kanal pasifken müşterinin gördüğü kapalı ekran */
                  <div className="flex-1 flex items-center justify-center px-5">
                    <div className="w-full bg-white rounded-3xl border border-zinc-100 shadow-lg p-6 flex flex-col items-center text-center">
                      <div className="size-12 rounded-full bg-white border-[3px] border-zinc-100 shadow-md overflow-hidden flex items-center justify-center mb-3">
                        <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
                      </div>
                      <span className="text-[10px] font-black tracking-[0.15em] text-zinc-950 uppercase">
                        {businessName}
                      </span>
                      <span className="text-[8px] font-bold text-zinc-700 mt-2">
                        Menümüz şu anda hizmet dışı.
                      </span>
                      <span className="text-[7px] text-zinc-400 mt-1">
                        Kısa süre içinde tekrar yayında olacağız.
                      </span>
                    </div>
                  </div>
                ) : previewTab === "kapak" ? (
                  /* Ana Sayfa: kapak, marka başlığı ve kategori ızgarası */
                  <div className="flex flex-col">
                    {/* Kapak görseli */}
                    <div className="relative w-full h-24 bg-zinc-100 shrink-0">
                      <img src={coverImageUrl} alt="Kapak" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-black/30" />
                      {isMultiLang && (
                        <span className="absolute top-2 right-2 backdrop-blur-md bg-white/70 text-zinc-950 px-2 py-1 rounded-full text-[7px] font-black tracking-widest uppercase shadow flex items-center gap-1 border border-white/40">
                          <Globe className="size-2" />{" "}
                          {defaultLanguage === "en" ? "English" : "Türkçe"}
                          <ChevronDown className="size-2 text-zinc-700" />
                        </span>
                      )}
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2">
                        <div className="size-12 rounded-full bg-white border-[3px] border-white shadow-lg overflow-hidden flex items-center justify-center">
                          <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    </div>

                    {/* İşletme başlığı */}
                    <div className="pt-8 pb-2 px-4 flex flex-col items-center text-center">
                      <h3
                        className="text-sm font-black tracking-[0.2em] uppercase leading-none"
                        style={{ color: secondaryColor }}
                      >
                        {businessName}
                      </h3>
                      <span
                        className="w-4 h-0.5 rounded-full mt-1.5"
                        style={{ backgroundColor: primaryColor }}
                      />
                      <p
                        className="text-[7px] font-extrabold uppercase tracking-widest mt-1.5"
                        style={{ color: primaryColor }}
                      >
                        Seçkin QR Menü Deneyimi
                      </p>
                      <p className="text-[7px] text-zinc-400 mt-0.5 italic line-clamp-1 px-4">
                        {businessDescription}
                      </p>
                    </div>

                    {/* Arama çubuğu */}
                    {showSearchBar && (
                      <div className="px-4 py-2 border-b border-zinc-100">
                        <div
                          className="flex items-center gap-1.5 bg-zinc-50 px-2.5 py-2 text-zinc-400 shadow-inner"
                          style={{ borderRadius: Math.max(6, Math.round(borderRadius * 0.4)) }}
                        >
                          <Search className="size-2.5 shrink-0" />
                          <span className="text-[8px] font-medium">
                            En sevdiğiniz lezzeti arayın...
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Aktif tanıtım afişi (önizleme) */}
                    {banners.filter((b) => b.visible).length > 0 && (
                      <div className="px-4 pt-2.5">
                        <div
                          className={`relative h-14 overflow-hidden ${previewCardShadow}`}
                          style={{ borderRadius: previewCardRadius }}
                        >
                          <img
                            src={banners.filter((b) => b.visible)[0].image}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                          <div className="absolute bottom-0 inset-x-0 p-1.5">
                            <span className="block font-extrabold text-white text-[8px] leading-tight drop-shadow-sm line-clamp-1">
                              {banners.filter((b) => b.visible)[0].title}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Kategori ızgarası */}
                    <div className="px-4 py-3 space-y-2">
                      <span className="block text-[7px] font-black uppercase tracking-[0.18em] text-zinc-400">
                        Menü Kategorileri
                      </span>
                      <div className="grid grid-cols-2 gap-2 pb-4">
                        {showFeaturedProducts && (
                          <div
                            className={`relative aspect-square overflow-hidden border border-zinc-100 ${previewCardShadow}`}
                            style={{ borderRadius: previewCardRadius }}
                          >
                            <img
                              src={menuCategories.find((c) => c.id === "favorites")?.image}
                              alt="Şefin Favorileri"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                            {showCategoryIcons && (
                              <div className="absolute top-1.5 left-1.5 size-5 rounded-full bg-white/90 flex items-center justify-center shadow-sm">
                                <Heart
                                  className="size-2.5 fill-current"
                                  style={{ color: primaryColor }}
                                />
                              </div>
                            )}
                            <div className="absolute bottom-0 inset-x-0 p-2">
                              <span className="block font-extrabold text-white text-[8px] leading-tight drop-shadow-sm">
                                Şefin Favorileri
                              </span>
                              <div className="flex items-center justify-between mt-0.5">
                                <span
                                  className="text-[6px] font-black uppercase tracking-widest px-1.5 py-0.5 text-white"
                                  style={{
                                    backgroundColor: hexToRgba(primaryColor, 0.9),
                                    borderRadius: previewChipRadius,
                                  }}
                                >
                                  {menuProducts.filter((p) => p.isFavorite).length} Özel Tarif
                                </span>
                                <ChevronRight className="size-2.5 text-white/70" />
                              </div>
                            </div>
                          </div>
                        )}

                        {menuCategories
                          .filter((c) => c.id !== "favorites" && PREVIEW_CATEGORY_LABELS[c.id])
                          .slice(0, showFeaturedProducts ? 5 : 6)
                          .map((cat) => {
                            const CatIcon = cat.icon;
                            const style =
                              PREVIEW_CATEGORY_STYLES[cat.id] ?? PREVIEW_CATEGORY_STYLES.coffee;
                            const count = menuProducts.filter(
                              (p) => p.categoryId === cat.id,
                            ).length;
                            return (
                              <div
                                key={cat.id}
                                className={`relative aspect-square overflow-hidden border border-zinc-100 ${previewCardShadow}`}
                                style={{ borderRadius: previewCardRadius }}
                              >
                                <img
                                  src={cat.image}
                                  alt={PREVIEW_CATEGORY_LABELS[cat.id]}
                                  className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                                {showCategoryIcons && (
                                  <div className="absolute top-1.5 left-1.5 size-5 rounded-full bg-white/90 flex items-center justify-center shadow-sm">
                                    <CatIcon className={`size-2.5 ${style.text}`} />
                                  </div>
                                )}
                                <div className="absolute bottom-0 inset-x-0 p-2">
                                  <span className="block font-extrabold text-white text-[8px] leading-tight drop-shadow-sm">
                                    {PREVIEW_CATEGORY_LABELS[cat.id]}
                                  </span>
                                  <div className="flex items-center justify-between mt-0.5">
                                    <span
                                      className="text-[6px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-white/25 text-white backdrop-blur-sm"
                                      style={{ borderRadius: previewChipRadius }}
                                    >
                                      {count} Özel Tarif
                                    </span>
                                    <ChevronRight className="size-2.5 text-white/70" />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    {/* İletişim & Konum footer (önizleme) */}
                    {(businessPhone ||
                      businessWhatsApp ||
                      businessInstagram ||
                      businessFacebook ||
                      businessWebsite ||
                      businessGmaps ||
                      businessAddress ||
                      workingHours) && (
                      <div className="px-4 pt-2.5 pb-3 border-t border-zinc-100 flex flex-col items-center text-center gap-1.5">
                        <div className="flex items-center gap-2">
                          {businessPhone && (
                            <span
                              className="size-5 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: hexToRgba(primaryColor, 0.1),
                                color: primaryColor,
                              }}
                            >
                              <Phone className="size-2.5" />
                            </span>
                          )}
                          {businessWhatsApp && (
                            <span
                              className="size-5 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: hexToRgba(primaryColor, 0.1),
                                color: primaryColor,
                              }}
                            >
                              <MessageSquare className="size-2.5" />
                            </span>
                          )}
                          {(businessInstagram || businessFacebook || businessWebsite) && (
                            <span
                              className="size-5 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: hexToRgba(primaryColor, 0.1),
                                color: primaryColor,
                              }}
                            >
                              <Globe className="size-2.5" />
                            </span>
                          )}
                          {businessGmaps && (
                            <span
                              className="size-5 rounded-full flex items-center justify-center"
                              style={{
                                backgroundColor: hexToRgba(primaryColor, 0.1),
                                color: primaryColor,
                              }}
                            >
                              <MapPin className="size-2.5" />
                            </span>
                          )}
                        </div>
                        {businessAddress && (
                          <p className="text-[6px] text-zinc-400 line-clamp-1 px-2">
                            {businessAddress}
                          </p>
                        )}
                        {workingHours && (
                          <p className="text-[6px] text-zinc-400 flex items-center gap-0.5">
                            <Clock className="size-2" /> {workingHours}
                          </p>
                        )}
                        <span className="text-[5px] text-zinc-400  font-bold">
                          Powered by <strong style={{ color: primaryColor }}>Vidyon</strong>
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Ürün Listesi: kategori detay görünümü */
                  <div className="flex flex-col flex-1">
                    {/* Kategori hero — büyük başlık fotoğrafın altında, geri butonu ikon rozeti */}
                    <div className="relative h-24 w-full shrink-0 overflow-hidden">
                      <img
                        src={menuCategories.find((c) => c.id === "coffee")?.image}
                        alt="Nitelikli Kahveler"
                        className="w-full h-full object-cover"
                      />
                      {/* Fotoğraf alta doğru zemine karışarak kaybolur (ana sayfa kapağı gibi) */}
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0) 40%, ${hexToRgba(backgroundColor, 0.55)} 72%, ${backgroundColor})`,
                        }}
                      />
                      <button
                        onClick={() => setPreviewTab("kapak")}
                        className="absolute top-2 left-2 size-6 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-zinc-800 shadow-sm"
                      >
                        <ChevronRight className="size-3 rotate-180" />
                      </button>
                      {showCategoryIcons && (
                        <div
                          className="absolute top-2 right-2 size-6 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm"
                          style={{ color: primaryColor }}
                        >
                          <Utensils className="size-3" />
                        </div>
                      )}
                      <div className="absolute bottom-0 inset-x-0 px-3 pb-1.5 flex flex-col items-center text-center">
                        <span
                          className="font-black uppercase tracking-[0.1em] text-[11px] whitespace-nowrap max-w-full overflow-hidden"
                          style={{ color: secondaryColor }}
                        >
                          Nitelikli Kahveler
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                      {menuProducts
                        .filter((p) => p.categoryId === "coffee")
                        .filter((p) => !(hideUnavailable && p.isAvailable === false))
                        .slice(0, 6)
                        .map((p) => {
                          return (
                            <div
                              key={p.id}
                              className={`flex items-center justify-between p-2.5 border border-zinc-100 ${previewCardShadow}`}
                              style={{
                                borderRadius: Math.max(6, Math.round(borderRadius * 0.5)),
                                ...previewCardSurface,
                              }}
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <img
                                  src={getProductImage(p)}
                                  alt={p.name}
                                  className="size-9 object-cover border border-zinc-100 shrink-0"
                                  style={{
                                    borderRadius: Math.max(4, Math.round(borderRadius * 0.35)),
                                  }}
                                />
                                <div className="min-w-0">
                                  <span
                                    className="block font-bold text-[9px] leading-tight"
                                    style={{ color: secondaryColor }}
                                  >
                                    {p.name}
                                  </span>
                                  <div className="flex flex-wrap items-center gap-0.5 mt-0.5">
                                    <span
                                      className="inline-flex items-center gap-0.5 text-[6px] font-extrabold uppercase tracking-widest px-1.5 py-0.5"
                                      style={{
                                        color: primaryColor,
                                        backgroundColor: hexToRgba(primaryColor, 0.1),
                                        borderRadius: previewChipRadius,
                                      }}
                                    >
                                      <Sparkles className="size-1.5 fill-current" /> Popüler
                                    </span>
                                    {showCalories && typeof p.calories === "number" && (
                                      <span
                                        className="inline-flex items-center text-[6px] font-bold px-1.5 py-0.5 bg-orange-50 text-orange-600"
                                        style={{ borderRadius: previewChipRadius }}
                                      >
                                        {p.calories} kcal
                                      </span>
                                    )}
                                    {showAllergens && (p.allergens?.length ?? 0) > 0 && (
                                      <span
                                        className="inline-flex items-center text-[6px] font-bold px-1.5 py-0.5 bg-red-50 text-red-600"
                                        style={{ borderRadius: previewChipRadius }}
                                      >
                                        {p.allergens!.join(", ")}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {!hidePrices && (
                                <span className="font-extrabold text-zinc-950 text-[9px]  bg-zinc-50 px-2 py-1 rounded-lg border border-zinc-100 shrink-0">
                                  {formatTRY(p.price)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
